import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { terminateSession } from "../utils/sessionManager";

// 🎯 Usage Tracker Context - Tracks student/user platform activity
export const UsageTrackerContext = createContext();

// Keep an inactive session for up to one hour before signing the user out.
const IDLE_LOGOUT_TIMEOUT_MS = 60 * 60 * 1000;

const readJsonObject = (key, fallback = {}) => {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || "null");
    return value && typeof value === "object" && !Array.isArray(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

const readSessionStart = () => {
  const value = Number(sessionStorage.getItem("sessionStart"));
  return Number.isFinite(value) && value > 0 ? value : Date.now();
};

export function UsageTrackerProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionStart, setSessionStart] = useState(() => {
    return readSessionStart();
  });
  const [lastActive, setLastActive] = useState(Date.now());
  const lastActivityEventAtRef = useRef(0);
  const lastFlushAtRef = useRef(0);
  const usageForbiddenRef = useRef(false);
  const [pageVisits, setPageVisits] = useState(() => {
    return readJsonObject("pageVisits");
  });

  // Track page visits
  useEffect(() => {
    if (!isAuthenticated) return;
    const path = window.location.pathname;
    setPageVisits((prev) => ({
      ...prev,
      [path]: (prev[path] || 0) + 1,
    }));
  }, []);

  // Save session start
  useEffect(() => {
    sessionStorage.setItem("sessionStart", sessionStart.toString());
  }, [sessionStart]);

  // Save page visits
  useEffect(() => {
    sessionStorage.setItem("pageVisits", JSON.stringify(pageVisits));
  }, [pageVisits]);

  // Track activity (user interaction). The idle timer is client-side UX only;
  // protected API routes must still enforce their own server-side session rules.
  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now();
      // Mouse movement is noisy; a timestamp every five seconds is enough to
      // keep a ten-minute idle timer accurate without causing constant renders.
      if (now - lastActivityEventAtRef.current < 5000) return;
      lastActivityEventAtRef.current = now;
      setLastActive(now);
    };

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
    events.forEach((e) => document.addEventListener(e, handleActivity));

    return () => events.forEach((e) => document.removeEventListener(e, handleActivity));
  }, [isAuthenticated]);

  // Authentication is confirmed by App only after a successful login or a
  // verified backend session restore. Anonymous visitors never log usage.
  useEffect(() => {
    const startTracking = () => {
      usageForbiddenRef.current = false;
      setIsAuthenticated(true);
      setSessionStart(Date.now());
      setPageVisits({});
    };
    const stopTracking = () => {
      usageForbiddenRef.current = false;
      setIsAuthenticated(false);
    };

    window.addEventListener("auth:session-established", startTracking);
    window.addEventListener("auth:logout", stopTracking);
    return () => {
      window.removeEventListener("auth:session-established", startTracking);
      window.removeEventListener("auth:logout", stopTracking);
    };
  }, []);

  // Auto logout after 10 minutes with no interaction.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const remainingMs = Math.max(0, IDLE_LOGOUT_TIMEOUT_MS - (Date.now() - lastActive));
    const timeout = window.setTimeout(() => {
      terminateSession({
        reason: "inactive",
        message: "You were logged out after 10 minutes of inactivity. Please login again.",
      });
    }, remainingMs);

    return () => window.clearTimeout(timeout);
  }, [isAuthenticated, lastActive]);

  // Calculate current session time
  const getCurrentSessionTime = useCallback(() => {
    return Math.floor((Date.now() - sessionStart) / 1000); // seconds
  }, [sessionStart]);

  // Get session duration in readable format
  const getSessionDuration = useCallback((seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  }, []);

  // Save usage to backend
  const saveUsageToBackend = useCallback(async () => {
    if (!isAuthenticated || usageForbiddenRef.current) return false;
    const sessionTime = getCurrentSessionTime();

    try {
      const response = await api.post(API_PATHS.usage.logSession, {
        sessionStart: new Date(sessionStart).toISOString(),
        sessionEnd: new Date().toISOString(),
        sessionDurationSeconds: sessionTime,
        pageVisits,
      }, {
        headers: { "Content-Type": "application/json" },
      });

      return response.ok;
    } catch (error) {
      if (error?.status === 403 || error?.response?.status === 403) {
        usageForbiddenRef.current = true;
      } else {
        console.error("Error saving usage:", error);
      }
      return false;
    }
  }, [getCurrentSessionTime, isAuthenticated, sessionStart, pageVisits]);

  // Save usage during page close/visibility change with keepalive
  const flushUsageNow = useCallback(() => {
    if (!isAuthenticated || usageForbiddenRef.current) return;
    const now = Date.now();
    // Browsers commonly emit visibilitychange, pagehide, and beforeunload for
    // one navigation. Send at most one usage event for that transition.
    if (now - lastFlushAtRef.current < 5000) return;
    lastFlushAtRef.current = now;
    const payload = {
      sessionStart: new Date(sessionStart).toISOString(),
      sessionEnd: new Date().toISOString(),
      sessionDurationSeconds: getCurrentSessionTime(),
      pageVisits,
    };

    api.request({
      url: API_PATHS.usage.logSession,
      method: "POST",
      keepalive: true,
      data: payload,
      headers: { "Content-Type": "application/json" },
    }).catch(() => {
      // Ignore fire-and-forget failures during unload.
    });
  }, [getCurrentSessionTime, isAuthenticated, pageVisits, sessionStart]);

  useEffect(() => {
    const handlePageHide = () => flushUsageNow();
    const handleBeforeUnload = () => flushUsageNow();
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        flushUsageNow();
      }
    };
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [flushUsageNow]);

  return (
    <UsageTrackerContext.Provider
      value={{
        sessionStart,
        lastActive,
        getCurrentSessionTime,
        getSessionDuration,
        saveUsageToBackend,
        pageVisits,
      }}
    >
      {children}
    </UsageTrackerContext.Provider>
  );
}

export function useUsageTracker() {
  const context = React.useContext(UsageTrackerContext);
  if (!context) {
    throw new Error("useUsageTracker must be used within UsageTrackerProvider");
  }
  return context;
}
